import { LabelType, NodeType, PinState, padding } from './constants';

/**
This method returns the node points in circular shape
it takes 5 inputs
1) xCenterPointOfOuterCircle: the x-axis center point of outer circle
2) yCenterPointOfOuterCircle: the y-axis center point of outer circle
3) radiusOfOuterCircle: radius of outer circle, the nodes will be in circular shape, size depends on this param
4) numberOfPoints: number of nodes to draw in the circular shape
5) initialAngle: initial angle/position of the starting node, other nodes will be mapped according to this angle
  **/
const getPointsInCircle = (
  xCenterPointOfOuterCircle: number,
  yCenterPointOfOuterCircle: number,
  radiusOfOuterCircle: number,
  numberOfPoints: number,
  initialAngle = -90,
  pinRadius = 10,
): NodeType[] => {
  const initialXAxisPoint = (initialAngle * Math.PI) / 180;
  const initialYAxisPoint = (initialAngle * Math.PI) / 180;
  return Array.from(
    { length: numberOfPoints },
    (_, i): NodeType => ({
      x:
        xCenterPointOfOuterCircle +
        radiusOfOuterCircle *
          Math.cos(initialXAxisPoint - (2 * i * Math.PI) / numberOfPoints) -
        pinRadius,
      y:
        yCenterPointOfOuterCircle +
        radiusOfOuterCircle *
          Math.sin(initialYAxisPoint - (2 * i * Math.PI) / numberOfPoints) -
        pinRadius,
    }),
  );
};

/**
This method returns the nodes in grid along with grid labels
**/
const getPointsInGridWithGridLabels = (
  width: number,
  height: number,
  noOfRows: number,
  noOfCols: number,
  pinDiameter: number,
): [NodeType[], LabelType[]] => {
  const gapInXAxis = (width - (noOfRows + 1) * pinDiameter - padding) / noOfRows;
  const gapInYAxis = (height - (noOfCols + 1) * pinDiameter - padding) / noOfCols;

  const points: NodeType[] = [];
  const labels: LabelType[] = [];
  const xStart = pinDiameter + gapInXAxis;
  const yStart = pinDiameter + gapInYAxis;

  for (let column = 1; column <= noOfRows; column++) {
    labels.push({
      x: column * pinDiameter + column * gapInXAxis,
      y: 0,
      text: `${String.fromCharCode(64 + column)}`,
    });
  }
  for (let row = 1; row <= noOfCols; row++) {
    labels.push({
      x: 0,
      y: row * pinDiameter + row * gapInYAxis,
      text: `${row}`,
    });
  }

  for (let row = 0; row < noOfRows; row++) {
    for (let column = 0; column < noOfCols; column++) {
      points.push({
        x: xStart + row * pinDiameter + row * gapInXAxis,
        y: yStart + column * pinDiameter + column * gapInYAxis,
      });
    }
  }
  return [points, labels];
};

/**
  This method returns the paths/lines column wise
  **/
const getPathsOfGridColumnWise = (
  width: number,
  height: number,
  noOfRows: number,
  noOfCols: number,
  pinDiameter: number,
) => {
  const pinRadius = pinDiameter / 2;
  const gapInXAxis = (width - (noOfRows + 1) * pinDiameter - padding) / noOfRows;
  const gapInYAxis = (height - (noOfCols + 1) * pinDiameter - padding) / noOfCols;
  return Array.from({ length: noOfRows }, (_, i) => [
    (i + 2) * pinDiameter - pinRadius + (i + 1) * gapInXAxis,
    pinDiameter + pinRadius + gapInYAxis,
    (i + 2) * pinDiameter - pinRadius + (i + 1) * gapInXAxis,
    height - padding - pinRadius,
  ]);
};

const getPinState = (
  occupiedPins: { crystalId: number; pinPosition: number; isDirty }[],
  pinPosition: number,
  selectedCrystalId: number,
) => {
  const pin = occupiedPins.find((item) => item.pinPosition === pinPosition);
  if (!pin) {
    return PinState.FREE;
  }
  if (pin.crystalId === selectedCrystalId && pin.isDirty) {
    return PinState.SELECTED;
  }
  if (pin.crystalId === selectedCrystalId && !pin.isDirty) {
    return PinState.CURRENT;
  }
  return PinState.OCCUPIED;
};

export {
  getPointsInCircle,
  getPointsInGridWithGridLabels,
  getPathsOfGridColumnWise,
  getPinState,
};
